<script setup>
import { computed } from 'vue';
import locales from 'shared/constants/locales';

const props = defineProps({
  showingOriginal: Boolean,
  // Present when the message was translated on its way out to the customer.
  outgoing: Boolean,
  sourceLanguage: { type: String, default: '' },
  targetLanguage: { type: String, default: '' },
});

defineEmits(['toggle']);

const nameFor = code => {
  const base = String(code).split(/[-_]/)[0];
  return locales[code] || locales[base] || code;
};

// An unlabelled "view original" tells the agent nothing about what the customer
// actually received, which is the one fact they need to trust the feature.
const stateLabel = computed(() => {
  if (!props.outgoing) return '';
  return props.showingOriginal ? 'ORIGINAL_SHOWN' : 'SENT_IN';
});

const languageName = computed(() =>
  nameFor(props.showingOriginal ? props.sourceLanguage : props.targetLanguage)
);

const toggleLabel = computed(() => {
  if (props.outgoing) {
    return props.showingOriginal
      ? 'CONVERSATION.TRANSLATION.SHOW_SENT'
      : 'CONVERSATION.TRANSLATION.SHOW_MY_ORIGINAL';
  }
  return props.showingOriginal
    ? 'CONVERSATION.VIEW_TRANSLATED'
    : 'CONVERSATION.VIEW_ORIGINAL';
});
</script>

<template>
  <span class="flex items-center gap-1.5 flex-wrap">
    <span
      v-if="outgoing"
      class="inline-flex items-center gap-1 text-xs text-n-slate-11"
    >
      <span class="i-lucide-languages size-3" />
      {{
        stateLabel === 'SENT_IN'
          ? $t('CONVERSATION.TRANSLATION.SENT_IN', { language: languageName })
          : $t('CONVERSATION.TRANSLATION.ORIGINAL_SHOWN', {
              language: languageName,
            })
      }}
    </span>
    <span
      class="text-xs text-n-slate-11 cursor-pointer hover:underline select-none"
      @click="$emit('toggle')"
    >
      {{ $t(toggleLabel) }}
    </span>
  </span>
</template>
